import {
  HHBox,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { WarningAmber } from '@mui/icons-material';
import { Theme, Tooltip } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import {
  LIVE_PROGRESS_SOURCE,
  PIPELINE_STAGE_ORDER,
  STAGE_STATUS,
} from '../constants/partner-data-gateway.constant';
import { StageHistoryEntry } from '../domain/types';
import { formatWireLabel } from '../utils/format.utils';
import { getToneColors, StatusTone } from '../utils/status-style.utils';

export const PIPELINE_TIMELINE_TEST_ID = 'pipeline-timeline';
export const PIPELINE_TIMELINE_UNAVAILABLE_WARNING_TEST_ID =
  'pipeline-timeline-unavailable-warning';
export const LIVE_PROGRESS_UNAVAILABLE_TOOLTIP =
  'Live progress data temporarily unavailable';
export const pipelineStageTestId = (stage: string): string =>
  `pipeline-stage-${stage}`;

const STAGE_RULE_WIDTH_PX = 3;
const STAGE_MIN_WIDTH_PX = 120;

interface PipelineTimelineProps {
  stageHistory: StageHistoryEntry[];
  /**
   * Detail-level `liveProgressSource`. PDGS reports progress availability
   * once per ingestion, not per stage, so the warning belongs on the
   * timeline as a whole.
   */
  liveProgressSource?: string;
}

/**
 * PDGS supplies `durationMs` directly; it is null while a stage is still
 * running.
 */
const formatDuration = (durationMs: number | null): string | undefined =>
  durationMs === null ? undefined : `${(durationMs / 1000).toFixed(1)}s`;

/**
 * Human-readable name for a stage status. Falls back to formatting the raw
 * value so a status PDGS adds later is still shown rather than silently
 * dropped.
 */
export const stageStatusLabel = (status?: string): string => {
  switch (status) {
    case STAGE_STATUS.Success:
      // 'Succeeded' rather than 'Completed': the pipeline also has a stage
      // *named* COMPLETED, and a screen reader would otherwise announce
      // 'Completed Completed' on that step.
      return 'Succeeded';
    case STAGE_STATUS.Failed:
      return 'Failed';
    case STAGE_STATUS.Cancelled:
      return 'Cancelled';
    case STAGE_STATUS.InProgress:
      return 'In progress';
    case STAGE_STATUS.Skipped:
      return 'Skipped';
    case undefined:
      return 'Not reached';
    default:
      return formatWireLabel(status);
  }
};

const STAGE_TONE: Record<string, StatusTone> = {
  [STAGE_STATUS.Success]: 'ok',
  [STAGE_STATUS.Failed]: 'bad',
  [STAGE_STATUS.Cancelled]: 'bad',
  [STAGE_STATUS.InProgress]: 'warn',
  [STAGE_STATUS.Skipped]: 'mute',
};

/** Undefined for a stage not yet reached, or a status this build predates. */
const stageTone = (status?: string): StatusTone | undefined =>
  status === undefined ? undefined : STAGE_TONE[status];

const stageStatusLine = (entry?: StageHistoryEntry): string => {
  const label = stageStatusLabel(entry?.status);
  const duration = formatDuration(entry?.durationMs ?? null);

  return duration ? `${label} · ${duration}` : label;
};

interface StageProps {
  stage: string;
  entry?: StageHistoryEntry;
}

/**
 * One cell of the stage strip: a coloured rule on top, the stage name, and
 * an outcome line. Status is carried by the text; the rule colour only
 * reinforces it.
 */
const Stage = ({ stage, entry }: StageProps): JSX.Element => {
  const tone = stageTone(entry?.status);

  return (
    <HHBox
      data-testid={pipelineStageTestId(stage)}
      data-tone={tone}
      sx={(theme: Theme): SystemStyleObject<Theme> => ({
        paddingTop: 2,
        borderTop: `${STAGE_RULE_WIDTH_PX}px solid ${
          tone ? getToneColors(theme, tone).fg : theme.palette.divider
        }`,
        borderRadius: '2px',
      })}
    >
      <HHTypography
        hhVariant="variant-bypass"
        variant="body2"
        sx={{ fontWeight: 500 }}
      >
        {formatWireLabel(stage)}
      </HHTypography>
      <HHTypography
        hhVariant="variant-bypass"
        variant="caption"
        component="p"
        sx={(theme: Theme): SystemStyleObject<Theme> => ({
          color:
            tone === 'bad'
              ? getToneColors(theme, tone).fg
              : theme.palette.text.secondary,
        })}
      >
        {stageStatusLine(entry)}
      </HHTypography>
    </HHBox>
  );
};

/**
 * Stage strip over the pipeline stages.
 *
 * The full stage list is rendered so stages the ingestion has not reached
 * yet show as "Not reached". PDGS returns `stageHistory` already sorted by
 * `startedAt`, and may add stages this build does not know about — those
 * are appended after the known ones rather than dropped, matching the BFF's
 * decision to expose `stage` as an open string.
 */
export const PipelineTimeline = ({
  stageHistory,
  liveProgressSource,
}: PipelineTimelineProps): JSX.Element => {
  const entriesByStage = new Map(
    stageHistory.map(entry => [entry.stage, entry]),
  );

  const unknownStages = stageHistory
    .map(entry => entry.stage)
    .filter(stage => !PIPELINE_STAGE_ORDER.includes(stage as never));

  const stages = [...PIPELINE_STAGE_ORDER, ...unknownStages];

  const isLiveProgressUnavailable =
    liveProgressSource === LIVE_PROGRESS_SOURCE.Unavailable;

  return (
    <HHBox data-testid={PIPELINE_TIMELINE_TEST_ID}>
      {isLiveProgressUnavailable && (
        <HHStack
          direction="row"
          alignItems="center"
          gap={0.5}
          paddingBottom={2}
        >
          <Tooltip title={LIVE_PROGRESS_UNAVAILABLE_TOOLTIP}>
            <WarningAmber
              data-testid={PIPELINE_TIMELINE_UNAVAILABLE_WARNING_TEST_ID}
              color="warning"
              fontSize="small"
            />
          </Tooltip>
          <HHTypography hhVariant="variant-bypass" variant="body2">
            {LIVE_PROGRESS_UNAVAILABLE_TOOLTIP}
          </HHTypography>
        </HHStack>
      )}
      <HHBox
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${STAGE_MIN_WIDTH_PX}px, 1fr))`,
          gap: 2,
        }}
      >
        {stages.map(stage => (
          <Stage key={stage} stage={stage} entry={entriesByStage.get(stage)} />
        ))}
      </HHBox>
    </HHBox>
  );
};
